/**
 * SafetyReview.tsx — where a clinician READS the safety wording patients are
 * shown, so that they can sign it off.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: medication safety
 * (2026-08-20).
 *
 * WHY THIS EXISTS. The wording behind `safety.clinicallyApproved` was authored
 * by our clinical lead and has to be RATIFIED by him — but until now the only
 * way to read it was to walk an entire patient journey: capture, questionnaire,
 * eligibility, an account, a plan, and finally the payment screen, which is
 * where the copy lives by design. No prescriber is going to create an account
 * and reach a card form to proof-read his own sentences, so the wording sat
 * unratified while real patients were being shown it. The gap was never the
 * content. It was that there was no surface on which the content could be READ
 * without buying something.
 *
 * WHY IT IS STILL A STAFF SECTION NOW THAT A PUBLIC SAFETY PAGE EXISTS
 * (safety/ClinicalSafetyPage.tsx, 2026-08-20). The two are not the same surface
 * wearing different gates, and this one does not become redundant:
 *
 *   - IT READS THE PRICED CATALOGUE. This section calls `getPlanOptions`, the
 *     eligibility-gated read that carries money and the clinic's commercial
 *     configuration. The public page reads a deliberately narrow projection with
 *     no price in it at all. Swapping this to the public read would lose the one
 *     thing a reviewer needs — a treatment ON SALE with no wording behind it —
 *     because a medication with no content is omitted from the public feed.
 *   - IT SHOWS THE UN-RATIFIED STATE TO STAFF as the contract requires, next to
 *     the plan it belongs to.
 *
 * So it stays a CLINICIAN-gated rail section on /clinicians, reached the way
 * every other section is, through the SAME authed call the prescription builder
 * and the recommend step already make — no new endpoint, and no new route by
 * which the PRICED catalogue can be reached.
 *
 * WHAT IT SHOWS, and the failure each one prevents:
 *
 *  1. THE WORDING VERBATIM, THROUGH THE PATIENT'S OWN RENDERER. He is not
 *     ratifying a summary; he is ratifying what a patient reads. So the urgent
 *     lines and every topic come from `checkout/MedicationSafetyGate`'s exported
 *     `PatientSafetyDetail` — the same component the payment screen and the
 *     public clinical-safety page mount. A second renderer here would be a review of a screen
 *     nobody is shown, and it would drift the first time either side is
 *     restyled, quietly voiding the sign-off it was built to collect.
 *
 *  2. THE CONTENT VERSION, PROMINENTLY. `safety.version` is what an
 *     acknowledgement is recorded against (`SafetyAcknowledgement.version`), so
 *     the version IS the thing being signed off. Ratifying "the safety wording"
 *     with no version attached leaves nothing to match against a year later when
 *     somebody asks which words a particular patient agreed to — which is the
 *     whole reason the acknowledgement carries a version at all.
 *
 *  3. A TREATMENT WITH NO AUTHORED CONTENT, LOUDLY. That is the most important
 *     row on this page: it is a hole in the estate's clinical copy, and the
 *     patient gate refuses the sale outright when it meets one. Listing only the
 *     treatments that HAVE content would let the hole pass sign-off unseen,
 *     which is exactly the failure this surface exists to catch.
 *
 * MONEY-REDACTION: the plan-options payload carries money. Nothing money-shaped
 * is rendered here — the money sweep in clinicians.spec.tsx covers this section
 * for the same reason it covers the prescription builder.
 *
 * R28: every medication name and every clinical sentence on this screen arrives
 * ON THE WIRE and is rendered as given. None of it is written into this file or
 * its comments — the R28 source scan (journey/r28.spec.tsx) reads comments and
 * test fixtures too.
 */
import { useCallback, useEffect, useState } from 'react';
import { getPlanOptions } from '../api/prices';
import type { PlanOption } from '../api/types';
import { PatientSafetyDetail, PatientYellowCardLine } from '../checkout/MedicationSafetyGate';
import { Row, SectionTitle } from '../ui/sections';
import { Alert, Badge, Card, ErrorState, Spinner } from '../ui/kit';
import { SafetySignoffNotice } from './SafetySignoffNotice';

type Loadable<T> = { state: 'loading' | 'ready' | 'error'; data: T | null };

/**
 * The three states a reviewer has to be able to tell apart at a glance, in
 * WORDS. Never a colour or an icon alone (WCAG 2.1 AA 1.4.1): "ratified" and
 * "awaiting sign-off" are the two halves of the decision this page exists to
 * collect, and a reviewer who cannot distinguish them without seeing green
 * versus amber cannot use the page at all.
 */
function signOffState(option: PlanOption): 'ratified' | 'awaiting' | 'absent' {
  if (!option.safety) return 'absent';
  return option.safety.clinicallyApproved ? 'ratified' : 'awaiting';
}

function SignOffBadge({ state }: { state: ReturnType<typeof signOffState> }) {
  if (state === 'ratified') return <Badge tone="positive">Ratified</Badge>;
  if (state === 'awaiting') return <Badge tone="pending">Awaiting sign-off</Badge>;
  return <Badge tone="danger">No safety information</Badge>;
}

export function SafetyReview({ clinicSlug }: { clinicSlug: string }) {
  const [options, setOptions] = useState<Loadable<PlanOption[]>>({ state: 'loading', data: null });

  // The SAME authed plan-options read the prescription builder and the recommend
  // step already make. Deliberately not a new "safety content" endpoint: a
  // second way to fetch post-eligibility clinical copy is a second thing to get
  // the authorisation wrong on, and this page needs nothing the existing read
  // does not already return.
  const load = useCallback(() => {
    setOptions({ state: 'loading', data: null });
    getPlanOptions(clinicSlug)
      .then((response) => setOptions({ state: 'ready', data: response.options }))
      .catch(() => setOptions({ state: 'error', data: null }));
  }, [clinicSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (options.state === 'loading') return <Spinner label="Loading the treatments and their safety information…" />;
  if (options.state === 'error' || !options.data) {
    // A blank page here would read as "nothing to sign off", which is the one
    // conclusion a failed read must never let a reviewer draw.
    return (
      <ErrorState
        message="Sorry — we could not load the treatments and their safety information."
        onRetry={load}
      />
    );
  }

  const list = options.data;
  const counts = {
    ratified: list.filter((option) => signOffState(option) === 'ratified').length,
    awaiting: list.filter((option) => signOffState(option) === 'awaiting').length,
    absent: list.filter((option) => signOffState(option) === 'absent').length,
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <Card>
        <SectionTitle>Patient safety information</SectionTitle>
        <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
          Every treatment this clinic offers, with the safety information a patient is shown on the
          payment screen before they can place an order. The words below are the words they read —
          this page renders them with the patient&rsquo;s own component rather than a copy of it, so
          what you sign off here is what is live.
        </p>
        <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
          Sign-off is recorded against the <strong>content version</strong> shown on each treatment.
          When the wording changes the version changes with it, and the new version needs signing off
          again.
        </p>

        {list.length === 0 ? (
          <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
            No treatments are configured for this clinic yet, so there is nothing to review.
          </p>
        ) : (
          <>
            <Row label="Treatments offered" value={list.length} />
            <Row label="Ratified" value={counts.ratified} />
            <Row label="Awaiting sign-off" value={counts.awaiting} />
            <Row label="No safety information authored" value={counts.absent} />
          </>
        )}
      </Card>

      {/* Contract obligation (contracts v0.9.0 `medicationSafety`): a consumer
          MUST surface `clinicallyApproved: false` to staff, on EVERY staff
          surface that reads plan options. This page states it per treatment as
          well — but leaving the shared notice off precisely here, on the one
          screen built around that field, is how a component designed to be
          impossible to half-apply ends up half-applied. */}
      <SafetySignoffNotice options={list} />

      {list.map((option) => {
        const state = signOffState(option);
        return (
          <Card key={option.productKey}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 'var(--gps-s-3)',
                margin: '0 0 14px',
              }}
            >
              {/* h3 under the card's h2 and above the topic h4s the patient
                  renderer emits — the document outline is how a screen-reader
                  user moves between treatments on a long page. */}
              <h3
                style={{
                  fontFamily: 'var(--gps-font-display)',
                  fontSize: '17px',
                  lineHeight: 1.25,
                  letterSpacing: '-0.01em',
                  margin: 0,
                  color: 'var(--gps-text)',
                }}
              >
                {option.medicationName}
              </h3>
              <div style={{ flex: 'none' }}>
                <SignOffBadge state={state} />
              </div>
            </div>

            <Row label="Treatment key" value={option.productKey} />

            {option.safety ? (
              <>
                <Row label="Content version" value={option.safety.version} />
                <Row
                  label="Sign-off state"
                  value={state === 'ratified' ? 'Ratified' : 'Awaiting sign-off'}
                />

                <div
                  style={{
                    background: 'var(--gps-surface-2)',
                    border: '1px solid var(--gps-border)',
                    borderRadius: 'var(--gps-r-lg)',
                    padding: 'var(--gps-s-4)',
                    marginTop: 'var(--gps-s-3)',
                  }}
                >
                  {/* The boundary of the verbatim region, stated in words. A
                      reviewer has to know where OUR framing stops and the
                      patient's screen starts, or he cannot tell which sentences
                      he is being asked to ratify. */}
                  <p
                    style={{
                      margin: '0 0 10px',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: 'var(--gps-muted)',
                    }}
                  >
                    Shown to the patient before they pay
                  </p>
                  <PatientSafetyDetail safety={option.safety} />
                  <PatientYellowCardLine yellowCardUrl={option.safety.yellowCardUrl} />
                </div>
              </>
            ) : (
              /* Deliberately the loudest thing this page can render. A missing
                 block is not a quiet edge case — it is a treatment on sale with
                 no risk information authored behind it, and the reviewer is the
                 only person who will notice. Block-level spans rather than
                 paragraphs because Alert wraps its children in a span. */
              <Alert tone="danger" style={{ marginTop: 'var(--gps-s-3)' }}>
                <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
                  No safety information is authored for this treatment.
                </strong>
                <span style={{ display: 'block' }}>
                  There is nothing for a patient to read and nothing here to sign off. The payment
                  screen refuses the order rather than selling this treatment unexplained, so it
                  cannot be bought until the wording is written.
                </span>
              </Alert>
            )}
          </Card>
        );
      })}
    </div>
  );
}
